import React, { useRef, useState } from 'react';
import {
  AttachmentPreview,
  BoxV2 as Box,
  Button,
  FilterType,
  Flex,
  RoleGuard,
  Table,
  TableFilter,
  useFilters,
  useTable,
  usePermission,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faGameConsoleHandheld,
} from '@fortawesome/pro-regular-svg-icons';
import { useParams } from 'react-router-dom';
import { useBrandContext } from '../../contexts/brand';

import BrandAssetsListingRow from './BrandAssetsListingRow';
import AssetActionModal, { AssetActionModalType } from './AssetActionModal';
import BrandAssetAppealModal from './BrandAssetAppealModal';
import {
  BrandAsset,
  BrandAssetExtended,
  BrandAssetStatus,
  BrandAssetType,
  AppealCategoryData,
  VettingAppeal,
  BrandStatus,
} from '../../types';
import {
  deleteBrandAsset,
  getAttachmentAsImageFile,
  getBrandAssets,
} from '../../apis';
import type { CreateAppealParams } from '@/apis/core';

import { toastFlashMessage } from '../../../../../utils';
import { ensureSortableFieldsExist, sort } from './utils';
import { brandAssetController, vettingController } from '@/apis/core';

const assetTypeOptions = Object.entries(BrandAssetType).map(
  ([label, value]) => ({
    label,
    value,
  })
);
const assetStatusOptions = [
  { label: 'N/A', value: 'N/A' },
  ...Object.entries(BrandAssetStatus).map(([label, value]) => ({
    label,
    value,
  })),
];

const FILTER_CONFIG = {
  type: {
    type: FilterType.Dropdown,
    label: 'Asset Type',
    placeholder: 'Select asset type',
    options: assetTypeOptions,
  },
  verificationStatus: {
    type: FilterType.Dropdown,
    label: 'Status',
    placeholder: 'Select status',
    options: assetStatusOptions,
  },
};

const TABLE_COLUMNS = [
  { id: 'name', label: 'ASSET NAME', sortable: false },
  { id: 'type', label: 'ASSET TYPE', sortable: true },
  { id: 'verificationStatus', label: 'STATUS', sortable: true },
  { id: 'createDate', label: 'CREATION DATE', sortable: true },
  { id: 'validUntil', label: 'EXPIRATION DATE', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false },
];

const PAGE_SIZE = 10;

interface Props {
  onOpenEmulator: () => void;
  appealCategories?: AppealCategoryData[];
  enabledCategoryIds?: string[];
  onAppealCreated: (appeal: VettingAppeal) => void;
  actionGuard?: (func: (...args: any[]) => any) => any;
}
const BrandAssetsList: React.FC<Props> = ({
  onOpenEmulator,
  appealCategories = [],
  enabledCategoryIds = [],
  onAppealCreated,
  actionGuard = (func: (...args: any[]) => any) =>
    (...args: any[]) =>
      func(...args),
}) => {
  const { id: brandId } = useParams<{ id: string }>();
  const [filtersShown, setFiltersShown] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<BrandAssetExtended>();
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number>();
  const [actionModal, setActionModal] = useState<AssetActionModalType>();
  const [openAppealModal, setOpenAppealModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const cachedAssetsRef = useRef<{ [id: string]: File }>({});
  const lastClickedAssetRef = useRef<string>();
  const { hasPermission } = usePermission();
  const { brandStatus, brandRefresh } = useBrandContext();
  const filter = useFilters({ configs: FILTER_CONFIG, inMemory: true });

  const table = useTable<BrandAsset>(
    async (additionalFilters) => {
      let assets: BrandAsset[] = await getBrandAssets(brandId);

      if (filter.appliedFilters) {
        const { type, verificationStatus } = filter.appliedFilters;
        if (type) {
          assets = assets.filter((asset) => asset.type === type);
        }
        if (verificationStatus) {
          if (verificationStatus === 'N/A') {
            assets = assets.filter((asset) => !asset.verificationStatus);
          } else {
            assets = assets.filter(
              (asset) => asset.verificationStatus === verificationStatus
            );
          }
        }
      }

      if (additionalFilters?.sortField) {
        const { sortField, ascendingOrder } = additionalFilters;
        assets = sort<BrandAssetExtended>(
          assets.map((asset) =>
            ensureSortableFieldsExist(
              asset,
              sortField as keyof BrandAssetExtended
            )
          ),
          sortField as keyof BrandAssetExtended,
          ascendingOrder
        );
      }

      const page = additionalFilters?.page || 1;
      const start = (page - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;

      return {
        data: assets.slice(start, end),
        rowsPerPage: PAGE_SIZE,
        total: assets.length,
        page,
      };
    },
    {
      deps: [filter.appliedFilters, brandRefresh],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
      inMemory: true,
    }
  );

  const updateCachedAssets = (attachmentUuid: string, file: File) => {
    cachedAssetsRef.current[attachmentUuid] = file;
  };

  const handlePreview = async (data: BrandAssetExtended, idx: number) => {
    const { attachmentUuid } = data;
    let file: File | undefined = cachedAssetsRef.current[attachmentUuid];
    setPreviewIndex(idx);
    lastClickedAssetRef.current = data.attachmentUuid;

    if (file) {
      setPreviewFile(file);
    } else {
      file = await getAttachmentAsImageFile(data);
      if (file) {
        updateCachedAssets(data.attachmentUuid, file);
        if (lastClickedAssetRef.current === data.attachmentUuid) {
          setPreviewFile(file);
        }
      } else {
        toastFlashMessage('Failed to fetch asset preview', 'error');
      }
    }
    setPreviewIndex(undefined);
  };

  const handleAssetActionClose = (data: BrandAssetExtended) => {
    data.file && updateCachedAssets(data.attachmentUuid, data.file);
    setActionModal(undefined);
    setSelectedAsset(undefined);
  };

  const handleAssetActionSubmit = async (data: BrandAssetExtended) => {
    data.file && updateCachedAssets(data.attachmentUuid, data.file);
    setProcessing(true);
    if (actionModal === 'verify') {
      const success = await brandAssetController.verifyAsset(
        brandId,
        data.name
      );
      if (success) {
        table.reload();
        toastFlashMessage(
          `${data.name} asset successfully submitted for verification`,
          'success'
        );
        setActionModal(undefined);
        setSelectedAsset(undefined);
      }
    } else {
      // Delete
      const response = await deleteBrandAsset(brandId, data.name);
      if (response) {
        table.reload();
        toastFlashMessage(`${data.name} asset deleted successfully`, 'success');
        setActionModal(undefined);
        setSelectedAsset(undefined);
      }
    }
    setProcessing(false);
  };

  const handleSubmitAppeal = async (data: CreateAppealParams) => {
    try {
      setProcessing(true);
      const appeal = await vettingController.createAppeal(brandId, 'asset', {
        ...data,
        name: selectedAsset?.name,
      });
      setProcessing(false);
      setOpenAppealModal(false);
      onAppealCreated(appeal);
      table.reload();
      toastFlashMessage(
        'Brand asset appeal request successfully completed',
        'success'
      );
    } catch {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 's',
          mb: 'xs',
        }}
      >
        <Button
          size="small"
          color="secondary"
          onClick={() => setFiltersShown(!filtersShown)}
          data-testid="brandAssetsFilterButton"
        >
          <FontAwesomeIcon icon={faFilter} />
          {filtersShown ? 'Hide' : 'Show'} Filters
        </Button>
        <RoleGuard feature="brandDetail.emulatorPreview">
          <Button
            size="small"
            color="secondary"
            onClick={actionGuard(() => onOpenEmulator())}
            data-testid="brandAssetsEmulatorButton"
          >
            <FontAwesomeIcon icon={faGameConsoleHandheld} />
            Emulator
          </Button>
        </RoleGuard>
      </Flex>
      {filtersShown && (
        <Box
          sx={{
            mt: 's',
            mb: 'xs',
            '& .MuiAutocomplete-root': { width: 'initial' },
          }}
        >
          <TableFilter
            configs={FILTER_CONFIG}
            candidateValues={filter.candidateFilters}
            appliedValues={filter.appliedFilters}
            onAppliedValuesChange={filter.handleApply}
            onCandidateValuesChange={filter.handleEdit}
            data-testid="brandAssetsFilter"
          />
        </Box>
      )}
      <Table
        testId="brandAssetsTable"
        loading={table.loading || processing}
        headRows={
          hasPermission('brandDetail.handleBrandAssets')
            ? TABLE_COLUMNS
            : TABLE_COLUMNS.filter((col) => col.id !== 'action')
        }
        disableHover
        tableData={table.tableData.map((asset, idx) => (
          <BrandAssetsListingRow
            key={`${idx}_${asset.attachmentUuid}`}
            data={asset}
            loading={previewIndex === idx}
            disableActions={brandStatus === BrandStatus.Deleted}
            onPreview={() => {
              setSelectedAsset(asset);
              handlePreview(asset, idx);
            }}
            onVerify={() => {
              setSelectedAsset({
                ...asset,
                file: cachedAssetsRef.current[asset.attachmentUuid],
              });
              setActionModal('verify');
            }}
            onDelete={() => {
              setSelectedAsset({
                ...asset,
                file: cachedAssetsRef.current[asset.attachmentUuid],
              });
              setActionModal('delete');
            }}
            onAppeal={() => {
              setSelectedAsset({
                ...asset,
                file: cachedAssetsRef.current[asset.attachmentUuid],
              });
              setOpenAppealModal(true);
            }}
          />
        ))}
        filter={table.filter}
        handleChangePage={table.handleChangePage}
        createSortHandler={table.handleSorting}
        pagination={table.pagination}
        emptyState="no assets to view"
        emptyBlockHeight="180px"
      />
      {previewFile && (
        <AttachmentPreview
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          mimeType="image/jpeg"
          style={{ zIndex: 1000 }}
        />
      )}
      {selectedAsset && actionModal && (
        <AssetActionModal
          open={!!selectedAsset}
          modalType={actionModal}
          data={selectedAsset}
          disabled={processing}
          onClose={handleAssetActionClose}
          onSubmit={handleAssetActionSubmit}
        />
      )}
      {openAppealModal && (
        <BrandAssetAppealModal
          open={openAppealModal}
          onClose={() => setOpenAppealModal(false)}
          onSubmit={handleSubmitAppeal}
          appealCategories={appealCategories}
          enabledCategoryIds={enabledCategoryIds}
          disabled={processing}
        />
      )}
    </Box>
  );
};

export default BrandAssetsList;
